/**
 * Utility functions for template filtering and sorting.
 */

/**
 * Filter templates based on selected statuses (client-side filtering).
 *
 * @param {Array} templates - Array of template objects
 * @param {string|Array} statusFilter - Status filter value(s)
 * @returns {Array} Filtered templates
 */
export function filterTemplatesByStatus(templates, statusFilter) {
  if (!templates || templates.length === 0) return [];

  // If statusFilter is 'all' or empty array, show all templates from this page
  if (
    statusFilter === 'all' ||
    (Array.isArray(statusFilter) && statusFilter.length === 0)
  ) {
    return templates;
  }

  // Normalize statusFilter to array if it's not already
  const selectedStatuses = Array.isArray(statusFilter)
    ? statusFilter
    : [statusFilter];

  return templates.filter(template => {
    const status = template.version_status || template.status;
    // Check if status matches any of the selected statuses
    // Support both lowercase and PromptVersion.* formats
    return selectedStatuses.some(selectedStatus => {
      if (selectedStatus === 'all') return true;
      return (
        status === selectedStatus ||
        status === `PromptVersion.${selectedStatus.toUpperCase()}` ||
        (selectedStatus === 'active' && status === 'PromptVersion.ACTIVE') ||
        (selectedStatus === 'draft' && status === 'PromptVersion.DRAFT') ||
        (selectedStatus === 'deprecated' &&
          status === 'PromptVersion.DEPRECATED') ||
        (selectedStatus === 'archived' && status === 'PromptVersion.ARCHIVED')
      );
    });
  });
}

/**
 * Sort templates based on sort criteria.
 *
 * @param {Array} templates - Array of template objects
 * @param {string} sortBy - Sort criteria
 * @returns {Array} Sorted templates
 */
export function sortTemplates(templates, sortBy) {
  if (!templates || templates.length === 0) return [];

  const sorted = [...templates];

  switch (sortBy) {
    case 'created_desc':
      sorted.sort((a, b) => {
        const dateA = new Date(a.created_at || a.version_created_at || 0);
        const dateB = new Date(b.created_at || b.version_created_at || 0);
        return dateB - dateA;
      });
      break;
    case 'created_asc':
      sorted.sort((a, b) => {
        const dateA = new Date(a.created_at || a.version_created_at || 0);
        const dateB = new Date(b.created_at || b.version_created_at || 0);
        return dateA - dateB;
      });
      break;
    case 'name_asc':
      sorted.sort((a, b) => {
        const nameA = (a.template_name || a.name || '').toLowerCase();
        const nameB = (b.template_name || b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      break;
    case 'name_desc':
      sorted.sort((a, b) => {
        const nameA = (a.template_name || a.name || '').toLowerCase();
        const nameB = (b.template_name || b.name || '').toLowerCase();
        return nameB.localeCompare(nameA);
      });
      break;
    case 'updated_desc':
      sorted.sort((a, b) => {
        const dateA = new Date(a.last_activity_at || a.version_created_at || 0);
        const dateB = new Date(b.last_activity_at || b.version_created_at || 0);
        return dateB - dateA;
      });
      break;
    case 'rating_desc':
      sorted.sort((a, b) => {
        const ratingA = a.avg_rating || a.analytics?.avg_rating || 0;
        const ratingB = b.avg_rating || b.analytics?.avg_rating || 0;
        // Sort by rating descending, but put 0 ratings at the end
        if (ratingA === 0 && ratingB === 0) return 0;
        if (ratingA === 0) return 1;
        if (ratingB === 0) return -1;
        return ratingB - ratingA;
      });
      break;
    default:
      // No sorting
      break;
  }

  return sorted;
}

/**
 * Normalize status filter for API vs UI.
 *
 * @param {string|Array} statusFilter - Status filter value(s)
 * @returns {Array} Normalized status array
 */
export function normalizeStatuses(statusFilter) {
  if (statusFilter === 'all') return [];
  return Array.isArray(statusFilter) ? statusFilter : [statusFilter];
}

/**
 * Get API status filter (supports single or multiple statuses as comma-separated string).
 *
 * @param {Array} normalizedStatuses - Normalized status array
 * @returns {string|null} API status filter (comma-separated) or null
 */
export function getApiStatusFilter(normalizedStatuses) {
  if (normalizedStatuses.length === 0) {
    return null; // "all" - no filter
  }
  // Return comma-separated string of statuses for server-side filtering
  // This allows the backend to filter before pagination, ensuring correct page sizes
  return normalizedStatuses.join(',');
}
